import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTalentos } from '../../../../application/talentos/hooks/useTalentos';
import ToolbarComponent from '../../../ui/section';
import Loader from '../../../ui/loader';
import { IconButton } from '../../../ui/button';
import { Row, Col } from '../../../ui/grid';
import Input from '../../../ui/input';
import NavigationService from '../../../../utils/history';
import { ModalConfirm } from '../../../ui/modal2';
import './style.css';

const CreditCodeDetailPage = () => {
    const { code } = useParams();
    const talentos = useTalentos();
    const {
        currentCreditCode,
        getCreditCodeById,
        deleteCreditCode,
        expireCreditCode,
        copyCreditCodeToClipboard
    } = talentos.creditCodes;

    const { getUserById } = talentos;

    const [isLoading, setIsLoading] = useState(true);
    const [employerData, setEmployerData] = useState(null);
    const [isLoadingEmployer, setIsLoadingEmployer] = useState(false);
    const [removeModal, setRemoveModal] = useState(false);
    const [expireModal, setExpireModal] = useState(false);

    useEffect(() => {
        if (code) {
            loadCode();
        }
    }, [code]);

    useEffect(() => {
        // Cuando se carga el código, obtener los datos del empleador
        if (currentCreditCode && currentCreditCode.assignedTo) {
            loadEmployerData(currentCreditCode.assignedTo);
        }
    }, [currentCreditCode]);

    const loadCode = async () => {
        if (!code) {
            console.error('No se proporcionó un código');
            return;
        }
        setIsLoading(true);
        try {
            const result = await getCreditCodeById(code);
            console.log('Código cargado:', result);
        } catch (error) {
            console.error('Error al cargar código:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadEmployerData = async (employerId) => {
        if (!employerId || typeof employerId !== 'string') {
            console.warn('ID de empleador inválido:', employerId);
            return;
        }

        setIsLoadingEmployer(true);
        try {
            const employer = await getUserById(employerId);
            console.log('Datos del empleador:', employer);
            if (employer) {
                // Si company es un objeto, obtener el nombre; si es string, usarlo directamente
                let companyName = null;
                if (employer.company) {
                    if (typeof employer.company === 'object' && employer.company.name) {
                        companyName = employer.company.name;
                    } else if (typeof employer.company === 'string' && !employer.company.match(/^[0-9a-fA-F]{24}$/)) {
                        // Si es un string pero no es un ID hexadecimal (MongoDB ObjectId), usarlo
                        companyName = employer.company;
                    }
                }
                
                setEmployerData({
                    name: employer.name || 'N/A',
                    email: employer.email || 'N/A',
                    company: companyName
                });
            } else {
                setEmployerData(null);
            }
        } catch (error) {
            console.error('Error al cargar datos del empleador:', error);
            setEmployerData(null);
        } finally {
            setIsLoadingEmployer(false);
        }
    };

    const handleExpire = async () => {
        try {
            await expireCreditCode(code);
            setExpireModal(false);
            await loadCode();
        } catch (error) {
            console.error('Error expiring code:', error);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteCreditCode(code);
            setRemoveModal(false);
            NavigationService.navigateTo('/talentos/credit-codes');
        } catch (error) {
            console.error('Error deleting code:', error);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            active: '#10b981',
            expired: '#f59e0b',
            disabled: '#ef4444'
        };
        return colors[status] || '#6b7280';
    };

    const getStatusLabel = (status) => {
        const labels = {
            active: 'Activo',
            expired: 'Expirado',
            disabled: 'Desactivado'
        };
        return labels[status] || status;
    };

    const handleBack = () => {
        NavigationService.navigateBack();
    };

    if (isLoading) {
        return (
            <ToolbarComponent label="Detalle de Código" showBackButton={true} onBackClick={handleBack}>
                <Loader text="Cargando código..." />
            </ToolbarComponent>
        );
    }

    if (!currentCreditCode) {
        return (
            <ToolbarComponent label="Detalle de Código" showBackButton={true} onBackClick={handleBack}>
                <Row padding="0 20px">
                    <Col md={12}>
                        <div style={{ textAlign: 'center', padding: '40px', color: '#dc2626' }}>
                            <p>No se encontró el código solicitado.</p>
                        </div>
                    </Col>
                </Row>
            </ToolbarComponent>
        );
    }

    return (
        <>
            <ToolbarComponent label={`Código: ${currentCreditCode.code}`} showBackButton={true} onBackClick={handleBack}>
                <Row padding="0 20px">
                    {/* Información principal - Columna izquierda */}
                    <Col md={8}>
                        <Input 
                            label="Código" 
                            type="text" 
                            value={currentCreditCode.code}
                            disabled={true}
                        />
                        <div style={{ marginTop: '8px', marginBottom: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton
                                onClick={() => {
                                    const claimLink = `${window.location.origin}/claim/${currentCreditCode.code}`;
                                    copyCreditCodeToClipboard(claimLink);
                                }}
                                tooltip="Copiar URL completa"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.33333 2.66667H11.3333C12.0697 2.66667 12.6667 3.26362 12.6667 4V10.6667C12.6667 11.403 12.0697 12 11.3333 12H5.33333C4.59695 12 4 11.403 4 10.6667V4C4 3.26362 4.59695 2.66667 5.33333 2.66667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M10.6667 2.66667V1.33333C10.6667 0.596954 10.0697 0 9.33333 0H2.66667C1.93029 0 1.33333 0.596954 1.33333 1.33333V8C1.33333 8.73638 1.93029 9.33333 2.66667 9.33333H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </IconButton>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: 'var(--main-text-color)', margin: '0 0 5px 0', fontWeight: 500 }}>Estado</label>
                            <input
                                type="text"
                                value={getStatusLabel(currentCreditCode.status)}
                                disabled={true}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: '1px solid #F4F7FE',
                                    borderRadius: 'var(--buttons-border-radius)',
                                    backgroundColor: '#F4F7FE',
                                    fontSize: '14px',
                                    color: getStatusColor(currentCreditCode.status),
                                    fontWeight: 600,
                                    outline: 'none'
                                }}
                            />
                        </div>

                        <Input 
                            label="Créditos" 
                            type="text" 
                            value={currentCreditCode.credits || 0}
                            disabled={true}
                        />

                        <Input 
                            label="Usos" 
                            type="text" 
                            value={`${currentCreditCode.usedCount || 0}/${currentCreditCode.maxUses || 1}`}
                            disabled={true}
                        />

                        <Input 
                            label="Creado" 
                            type="text" 
                            value={currentCreditCode.createdAt ? new Date(currentCreditCode.createdAt).toLocaleString('es-ES') : 'N/A'}
                            disabled={true}
                        />

                        <Input 
                            label="Expira" 
                            type="text" 
                            value={currentCreditCode.expiresAt ? new Date(currentCreditCode.expiresAt).toLocaleString('es-ES') : 'Nunca'}
                            disabled={true}
                        />

                        {currentCreditCode.description && (
                            <Input 
                                label="Descripción" 
                                type="textarea" 
                                value={currentCreditCode.description}
                                disabled={true}
                                rows={3}
                            />
                        )}

                        {currentCreditCode.claimLink && (
                            <div>
                                <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Enlace de Reclamo</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        value={currentCreditCode.claimLink}
                                        readOnly
                                        style={{
                                            flex: 1,
                                            padding: '10px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            background: '#f9fafb'
                                        }}
                                    />
                                    <IconButton
                                        onClick={() => copyCreditCodeToClipboard(currentCreditCode.claimLink)}
                                        tooltip="Copiar enlace"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5.33333 2.66667H11.3333C12.0697 2.66667 12.6667 3.26362 12.6667 4V10.6667C12.6667 11.403 12.0697 12 11.3333 12H5.33333C4.59695 12 4 11.403 4 10.6667V4C4 3.26362 4.59695 2.66667 5.33333 2.66667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M10.6667 2.66667V1.33333C10.6667 0.596954 10.0697 0 9.33333 0H2.66667C1.93029 0 1.33333 0.596954 1.33333 1.33333V8C1.33333 8.73638 1.93029 9.33333 2.66667 9.33333H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </IconButton>
                                </div>
                            </div>
                        )}
                    </Col>

                    {/* Información del empleador - Columna derecha */}
                    <Col md={4}>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '12px', fontSize: '16px' }}>
                                Empleador Asignado
                            </label>
                            {isLoadingEmployer ? (
                                <div style={{
                                    background: '#f9fafb',
                                    padding: '16px',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    textAlign: 'center',
                                    color: '#6b7280',
                                    fontSize: '14px'
                                }}>
                                    Cargando...
                                </div>
                            ) : employerData ? (
                                <div style={{
                                    background: '#f9fafb',
                                    padding: '16px',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb'
                                }}>
                                    <div style={{ marginBottom: '12px' }}>
                                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Nombre</div>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>
                                            {employerData.name}
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Email</div>
                                        <div style={{ fontSize: '14px', color: '#4b5563' }}>
                                            {employerData.email}
                                        </div>
                                    </div>
                                    {employerData.company && (
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Empresa</div>
                                            <div style={{ fontSize: '14px', color: '#4b5563' }}>
                                                {employerData.company}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{
                                    background: '#fef2f2',
                                    padding: '16px',
                                    borderRadius: '6px',
                                    border: '1px solid #fecaca',
                                    color: '#991b1b',
                                    fontSize: '14px'
                                }}>
                                    No se encontró información del empleador asignado
                                </div>
                            )}
                        </div>

                        {/* Historial de usos */}
                        {currentCreditCode.usedBy && currentCreditCode.usedBy.length > 0 && (
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '12px', fontSize: '16px' }}>
                                    Historial de Usos
                                </label>
                                <div style={{
                                    background: '#f9fafb',
                                    padding: '16px',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    maxHeight: '200px',
                                    overflowY: 'auto'
                                }}>
                                    {currentCreditCode.usedBy.map((usage, index) => (
                                        <div key={index} style={{
                                            padding: '8px 0',
                                            borderBottom: index < currentCreditCode.usedBy.length - 1 ? '1px solid #e5e7eb' : 'none'
                                        }}>
                                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                Reclamado: {new Date(usage.claimedAt).toLocaleString('es-ES')}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Acciones */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {currentCreditCode.status === 'active' && (
                                <button
                                    onClick={() => setExpireModal(true)}
                                    style={{
                                        background: '#f59e0b',
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        width: '100%'
                                    }}
                                >
                                    Expirar Código
                                </button>
                            )}
                            <button
                                onClick={() => setRemoveModal(true)}
                                style={{
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    width: '100%'
                                }}
                            >
                                Desactivar Código
                            </button>
                        </div>
                    </Col>
                </Row>
            </ToolbarComponent>

            <ModalConfirm
                isOpen={expireModal}
                onClose={() => setExpireModal(false)}
                onAccept={handleExpire}
                title="¿Expirar código?"
                message="Esta acción marcará el código como expirado."
            />

            <ModalConfirm
                isOpen={removeModal}
                onClose={() => setRemoveModal(false)}
                onAccept={handleDelete}
                title="¿Desactivar código?"
                message="Esta acción desactivará el código permanentemente. No se puede deshacer."
            />
        </>
    );
};

export default CreditCodeDetailPage;
